import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { App, Button, Form, Input, Tooltip } from 'antd'
import { useTRPC } from '@/trpc/react.ts'
import type { GroupDetailData } from './finance.types.ts'
import type { GroupBillTableRow } from './group-bill-columns.tsx'
import { GroupBillPicker } from './group-bill-picker.tsx'
import { GroupBillsTable } from './group-bills-table.tsx'
import { OrganizationAccountSelect } from './organization-account-select.tsx'
import { snapshotObject } from './utils/finance-snapshot.ts'
import { fromCents, toCents } from './utils/money.ts'

export function GroupRevisionEditor({
  group: initialGroup,
  onClose,
  onSaved
}: {
  group: GroupDetailData
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  // 编辑内容与并发校验版本属于同一次会话，不随后台详情刷新改变。
  const [group] = useState(initialGroup)
  const [open, setOpen] = useState(true)
  const [saved, setSaved] = useState(false)
  const trpc = useTRPC()
  const { message } = App.useApp()
  const draft = snapshotObject(group.revisionDraft)
  type Row = GroupBillTableRow
  const [rows, setRows] = useState<Row[]>(() =>
    Array.isArray(draft.items)
      ? (draft.items as Row[]).map((item) => ({
          ...group.items.find((current) => current.billId === item.billId),
          ...item,
          feeType: item.feeType ?? (snapshotObject(item.billSnapshot).feeType as Row['feeType'])
        }))
      : group.items.filter((i) => !i.releasedAt)
  )
  const [ids, setIds] = useState(() =>
    rows.filter((i) => toCents(i.outstandingAmount) > BigInt(0)).map((i) => i.billId)
  )
  const selected = new Set(ids)
  const total = rows.reduce(
    (sum, row) => sum + (selected.has(row.billId) ? toCents(row.outstandingAmount) : BigInt(0)),
    BigInt(0)
  )
  const [picking, setPicking] = useState(false)
  const save = useMutation(trpc.bill.saveGroupRevision.mutationOptions())
  return (
    <>
      <ModalForm<{ note?: string; reason: string; collectionAccountId?: number }>
        open={open}
        onOpenChange={setOpen}
        title="调整组内账单 · 发布后生效"
        layout="vertical"
        initialValues={{
          note: draft.note ?? group.note,
          collectionAccountId: draft.collectionAccountId ?? group.collectionAccountId,
          reason: draft.reason
        }}
        modalProps={{
          width: '60rem',
          closable: !save.isPending,
          keyboard: !save.isPending,
          onCancel: () => setOpen(false),
          destroyOnHidden: true,
          mask: { closable: false },
          afterClose: () => {
            void (async () => {
              try {
                if (saved) {
                  await onSaved()
                }
              } catch {
                void message.error('修订已保存，刷新失败，请刷新页面')
              }
              onClose()
            })()
          }
        }}
        submitter={{
          searchConfig: { submitText: '保存修订草稿' },
          resetButtonProps: { disabled: save.isPending },
          submitButtonProps: { loading: save.isPending, disabled: !ids.length }
        }}
        onFinish={async (values) => {
          try {
            await save.mutateAsync({
              groupId: group.id,
              expectedUpdatedAt: group.updatedAt,
              billIds: ids,
              collectionAccountId: values.collectionAccountId ?? null,
              note: values.note ?? null,
              reason: values.reason
            })
            setSaved(true)
            setOpen(false)
            return true
          } catch (e) {
            void message.error(e instanceof Error ? e.message : '修订保存失败')
            return false
          }
        }}
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-medium">
              {group.customerName} · 已选 {ids.length} 张 · 待收 ¥{fromCents(total)}
            </div>
            <p className="text-content-muted mt-1 mb-0 text-xs">
              保存后发布生效，旧版本保留。已结清账单不纳入本次修订。
              <Tooltip title="移出账单不减免应收；催收状态请在账单组的催收设置中修改。">
                <span tabIndex={0} className="cursor-help underline decoration-dotted underline-offset-4">
                  调整说明
                </span>
              </Tooltip>
            </p>
          </div>
          <Button onClick={() => setPicking(true)} disabled={save.isPending}>
            添加账单
          </Button>
        </div>
        <GroupBillsTable
          items={rows}
          amountField="outstandingAmount"
          amountTitle="当前待收"
          rowClassName={(row) => (selected.has(row.billId) ? '' : 'opacity-50')}
          extraColumns={[
            {
              fixed: 'right',
              align: 'center',
              title: '操作',
              width: '6.25rem',
              key: 'membership',
              render: (_, row) => (
                <Button
                  type="link"
                  disabled={save.isPending || toCents(row.outstandingAmount) <= BigInt(0)}
                  onClick={() =>
                    setIds((previous) =>
                      previous.includes(row.billId)
                        ? previous.filter((id) => id !== row.billId)
                        : [...previous, row.billId]
                    )
                  }
                >
                  {toCents(row.outstandingAmount) <= BigInt(0)
                    ? '已结清'
                    : ids.includes(row.billId)
                      ? '移出本组'
                      : '重新加入'}
                </Button>
              )
            }
          ]}
        />
        <Form.Item className="mt-4" name="collectionAccountId" label="收款账户" rules={[{ required: true }]}>
          <OrganizationAccountSelect purpose="bill.group.read" organizationId={group.organizationId} />
        </Form.Item>
        <Form.Item name="reason" label="修订原因" rules={[{ required: true, whitespace: true }]}>
          <Input.TextArea rows={2} maxLength={1000} placeholder="说明本次添加或移出账单的原因" />
        </Form.Item>
        <Form.Item name="note" label="出单备注">
          <Input.TextArea rows={2} maxLength={5000} />
        </Form.Item>
      </ModalForm>
      {picking && (
        <GroupBillPicker
          group={group}
          onClose={() => setPicking(false)}
          onSelect={(bills) => {
            const added = bills
              .filter((b) => !rows.some((r) => r.billId === b.id))
              .map((b) => ({
                billId: b.id,
                billVersion: b.version,
                outstandingAmount: b.outstandingAmount,
                feeType: b.feeType,
                collectionStatus: b.collectionStatus,
                latestSettlementAt: b.latestSettlementAt,
                outstandingAmountSnapshot: b.outstandingAmount,
                releasedAt: null,
                billSnapshot: b
              }))
            setRows([...rows, ...added])
            setIds([...new Set([...ids, ...bills.map((b) => b.id)])])
          }}
        />
      )}
    </>
  )
}
